import React from "react";
import { Clock, User, Scissors } from "lucide-react";
import GlobalLoader from "../ui/GlobalLoader";

type BookingLike = {
  id: string | number;
  customer: string;
  time: string;
  amount: number;
  services_label?: string;
  staff?: string;
  status?: string;
};

type ChairRow = {
  barber: string;
  current?: BookingLike;
  next?: BookingLike;
};

type MyChairTodayProps = {
  title?: string;
  chairs: ChairRow[];
  loading?: boolean;
  currencySymbol?: string;
  onBookingClick?: (bookingId: string | number) => void;
  singleBarberMode?: boolean;
};

function statusLabel(status?: string): string {
  if (!status) return "Upcoming";
  if (status === "in progress") return "In chair";
  if (status === "booked" || status === "pending") return "Up next";
  return status;
}

const MyChairToday = ({
  title = "Today's Schedule",
  chairs,
  loading = false,
  currencySymbol = "",
  onBookingClick,
  singleBarberMode = false,
}: MyChairTodayProps) => {
  return (
    <div className="bg-white rounded-xl shadow-md p-6">
      <h2 className="text-lg font-semibold text-[#402B69] mb-4">{title}</h2>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <GlobalLoader fullScreen={false} description="Loading chair status..." compensateSidebar={false} />
        </div>
      ) : chairs.length === 0 ? (
        <p className="text-sm text-gray-500 text-center py-8">No appointments scheduled for today.</p>
      ) : (
        <div className={`grid gap-3 ${singleBarberMode ? "grid-cols-1" : "grid-cols-1 md:grid-cols-2"}`}>
          {chairs.map((chair) => {
            const active = chair.current ?? chair.next;
            return (
              <div
                key={chair.barber}
                className="rounded-xl border border-[#EDE3F7] bg-[#FAF7FD] p-4"
              >
                <div className="flex items-center justify-between mb-3">
                  <p className="font-semibold text-[#402B69]">{chair.barber}</p>
                  {active?.status && (
                    <span className="text-[10px] font-bold uppercase tracking-wide px-2 py-1 rounded-full bg-[#EDE3F7] text-[#885ABB]">
                      {statusLabel(active.status)}
                    </span>
                  )}
                </div>

                {active ? (
                  <button
                    type="button"
                    onClick={() => onBookingClick?.(active.id)}
                    className="w-full text-left rounded-lg bg-white border border-gray-100 p-3 hover:border-[#885ABB]/40 hover:shadow-sm transition"
                  >
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <p className="font-semibold text-[#1E1E1E] flex items-center gap-1.5">
                        <User size={14} className="text-[#885ABB]" />
                        {active.customer}
                      </p>
                      <p className="font-semibold text-[#885ABB] whitespace-nowrap">
                        {currencySymbol}{active.amount.toFixed(2)}
                      </p>
                    </div>
                    <p className="text-sm text-gray-600 flex items-center gap-1.5 mb-1">
                      <Scissors size={14} className="text-[#885ABB]" />
                      {active.services_label ?? "—"}
                    </p>
                    <p className="text-xs text-gray-500 flex items-center gap-1.5">
                      <Clock size={13} className="text-[#885ABB]" />
                      {active.time}
                    </p>
                  </button>
                ) : (
                  <p className="text-sm text-gray-400">No appointments for this team member today.</p>
                )}

                {chair.current && chair.next && (
                  <div className="mt-3 pt-3 border-t border-[#EDE3F7]">
                    <p className="text-[10px] uppercase tracking-wide text-gray-400 font-bold mb-1">Next up</p>
                    <button
                      type="button"
                      onClick={() => onBookingClick?.(chair.next!.id)}
                      className="text-sm text-[#402B69] hover:text-[#885ABB] transition"
                    >
                      {chair.next.time} — {chair.next.customer}
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export function buildChairRowsFromBookings(bookings: BookingLike[]): ChairRow[] {
  const byBarber = new Map<string, BookingLike[]>();

  bookings.forEach((booking) => {
    const barber = booking.staff || "Unassigned";
    const list = byBarber.get(barber) ?? [];
    list.push(booking);
    byBarber.set(barber, list);
  });

  return Array.from(byBarber.entries())
    .map(([barber, rows]) => {
      const sorted = [...rows].sort((a, b) => a.time.localeCompare(b.time));
      const current = sorted.find((row) => row.status === "in progress");
      const upcoming = sorted.filter((row) => row.status !== "in progress");
      return {
        barber,
        current,
        next: current ? upcoming[0] : sorted[0],
      };
    })
    .sort((a, b) => a.barber.localeCompare(b.barber));
}

export default MyChairToday;
